// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import {
  StatusSchema,
  ResponseMessageSchema,
  PaginationInfoSchema,
  coerceBoolean,
} from "./utilitySchemas";
import { RemindersSchema } from "./remindersSchema";

export const CommentTypeEnum = z.enum(["ADMIN_COMMENT", "USER_REMARK", "TASK_REMINDER"]);
export type CommentType = z.infer<typeof CommentTypeEnum>;
export const CommentScopeEnum = z.enum(["REQUEST", "ENTITY", "USER"]);
export type CommentScope = z.infer<typeof CommentScopeEnum>;

const BaseCommentFields = {
  content: z
    .string()
    .min(1, { message: "Comment content is required" })
    .max(5000, {
      message: "Comment content cannot exceed 5000 characters",
    }),
  mentionedUserIds: z.array(z.number().int().positive()).optional(),
  isExternal: coerceBoolean(),
  optimisticClientId: z.string().uuid().optional(),
  type: CommentTypeEnum,
};

export const RequestCommentSchema = z.object({
  ...BaseCommentFields,
  scope: z.literal("REQUEST"),
  scopeId: z.string().uuid({
    message: "requestId is required for REQUEST comments",
  }),
});

export const EntityCommentSchema = z.object({
  ...BaseCommentFields,
  scope: z.literal("ENTITY"),
  scopeId: z.coerce.number().int().positive({
    message: "entityId is required for ENTITY comments",
  }),
});

export const UserCommentSchema = z.object({
  ...BaseCommentFields,
  scope: z.literal("USER"),
  scopeId: z.coerce.number().int().positive({
    message: "userId is required for USER comments",
  }),
});

export const BaseCommentContextSchema = z.discriminatedUnion("scope", [
  RequestCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }),
  EntityCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }),
  UserCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }),
]);

export type BaseCommentContext = z.infer<typeof BaseCommentContextSchema>

export const CreateCommentSchema = z.discriminatedUnion("scope", [
  RequestCommentSchema,
  EntityCommentSchema,
  UserCommentSchema,
]);

export type CreateComment = z.infer<typeof CreateCommentSchema>;

export const CommentsQueryContextSchema = z.discriminatedUnion("scope", [
  RequestCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }).extend({
    include: CommentScopeEnum.array().optional(),
  }),
  EntityCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }).extend({
    include: CommentScopeEnum.array().optional(),
  }),
  UserCommentSchema.pick({ scope: true, scopeId: true, isExternal: true }).extend({
    include: CommentScopeEnum.array().optional(),
  }),
]);

export type CommentsQueryContext = z.infer<typeof CommentsQueryContextSchema>;

export const CommentQuerySchema = z.object({
  scope: CommentsQueryContextSchema,
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  filter: z.object({
    type: z.array(CommentTypeEnum).optional(),
  }).optional()
});
export type CommentQuery = z.infer<typeof CommentQuerySchema>;

const CommentRoleSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const CommentUserSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  role: CommentRoleSchema,
});

export const CommentSchema = z.object({
  id: z.string(),
  optimisticClientId: z.string().uuid().optional(),
  mentionedUserIds: z.array(z.number()),
  content: z.string(),
  evalionId: z.string(),
  createdAt: z.string(),
  pinnedAt: z.string().nullable().optional(),
  pinnedByUserId: z.number().nullable().optional(),
  pinnedByUser: CommentUserSchema.nullable().optional(),
  isPinned: z.boolean().optional(),
  isPinnedViewed: z.boolean().nullable().optional(),
  scopeId: z.union([z.string(), z.number()]),
  type: CommentTypeEnum,
  scope: CommentScopeEnum,
  userId: z.number().nullable(),
  user: CommentUserSchema.nullable(),
  isExternal: z.boolean(),
  requestEntityId: z.number().nullable(),
  reminder: RemindersSchema.nullable(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const CommentResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: CommentSchema,
  pagination: PaginationInfoSchema.optional(),
});
export type CommentResponse = z.infer<typeof CommentResponseSchema>;

const CommentsMetaSchema = z.object({
  totalCount: z.number().int().nonnegative(),
  totalPinnedCount: z.number().int().nonnegative().optional(),
  unreadPinnedCount: z.number().int().nonnegative().optional(),
  lastReadPinnedAt: z.string().nullable().optional(),
  lastReadPinnedCommentId: z.string().nullable().optional(),
});
export  type CommentsMeta = z.infer<typeof CommentsMetaSchema>;
export const CommentsListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: z.object({
    comments: z.array(CommentSchema),
    meta: CommentsMetaSchema.optional(),
  }),
  pagination: PaginationInfoSchema.optional(),
});
export type CommentsListResponse = z.infer<
  typeof CommentsListResponseSchema
>;


export const SendCommentNotificationEmailSchema = z.object({
  emailType: z.enum([
    "newUserRemark",
  ]),
  isExternalRequest: z.boolean().default(false),
});

export type SendCommentNotificationEmail = z.infer<typeof SendCommentNotificationEmailSchema>;

export const CommentReadStateSchema = z.object({
  userId: z.number(),
  scope: CommentScopeEnum,
  scopeId: z.union([z.string(), z.number()]),
  lastReadAt: z.string(),
  isExternal: z.boolean(),
});
export type CommentReadState = z.infer<typeof CommentReadStateSchema>;
export const CommentReadStateResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: CommentReadStateSchema,
});
export type CommentReadStateResponse = z.infer<
  typeof CommentReadStateResponseSchema
>;

export const CommentReadStatesListResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: z.object({
    commentReadStates: z.array(CommentReadStateSchema),
  }),
});
export type CommentReadStatesListResponse = z.infer<
  typeof CommentReadStatesListResponseSchema
>;

export const UpdateCommentReadStateSchema = z.object({
  lastReadAt: z.string(),
});
export type UpdateCommentReadState = z.infer<
  typeof UpdateCommentReadStateSchema
>;
export const CreateCommentReadStateSchema = z.object({
  scope: CommentScopeEnum,
  scopeId: z.union([z.string(), z.number()]),
  isExternal: z.boolean().default(false),
  lastReadAt: z.string(),
});
export type CreateCommentReadState = z.infer<
  typeof CreateCommentReadStateSchema
>;

export const CommentReadStateQuerySchema = z.object({
  scope: CommentScopeEnum.optional(),
  scopeId: z.union([z.string(), z.number()]).optional(),
  isExternal: z.boolean().default(false).optional(),
});
export type CommentReadStateQuery = z.infer<
  typeof CommentReadStateQuerySchema
>;

export const UpdateCommentPinSchema = z.object({
  isExternal: z.boolean().default(false),
  pinned: z.boolean(),
});

export type UpdateCommentPin = z.infer<typeof UpdateCommentPinSchema>;

export const PinnedCommentReadStateSchema = z.object({
  userId: z.number(),
  scope: CommentScopeEnum,
  scopeId: z.union([z.string(), z.number()]),
  lastReadPinnedAt: z.string(),
  isExternal: z.boolean(),
});

export type PinnedCommentReadState = z.infer<
  typeof PinnedCommentReadStateSchema
>;

export const PinnedCommentReadStateStatsSchema = z.object({
  scope: CommentScopeEnum,
  scopeId: z.union([z.string(), z.number()]),
  isExternal: z.boolean(),
  lastReadPinnedAt: z.string().nullable(),
  lastReadPinnedCommentId: z.string().nullable(),
  totalPinnedCount: z.number().int().nonnegative(),
  unreadPinnedCount: z.number().int().nonnegative(),
});

export type PinnedCommentReadStateStats = z.infer<
  typeof PinnedCommentReadStateStatsSchema
>;

export const PinnedCommentReadStateResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema.optional(),
  data: PinnedCommentReadStateStatsSchema,
});
export type PinnedCommentReadStateResponse = z.infer<
  typeof PinnedCommentReadStateResponseSchema
>;

export const CreatePinnedCommentReadStateSchema = z
  .object({
    lastReadPinnedCommentId: z.string().uuid(),
  });

export type CreatePinnedCommentReadState = z.infer<
  typeof CreatePinnedCommentReadStateSchema
>;

export const PinnedCommentReadStateQuerySchema = z.object({
  scope: CommentScopeEnum,
  scopeId: z.union([z.string(), z.number()]),
  isExternal: z.boolean().default(false).optional(),
  include: z.array(CommentScopeEnum).optional(),
});

export type PinnedCommentReadStateQuery = z.infer<
  typeof PinnedCommentReadStateQuerySchema
>;
